import { useEffect, useRef } from "react";

/**
 * Barra fina de progresso do scroll, ancorada logo abaixo da TopBar.
 * Mede a altura real da TopBar (que pode quebrar em 2 linhas no mobile)
 * para nunca sobrepor a copy.
 *
 * PERF: nem o progresso nem o offset passam por estado do React. A versão
 * anterior chamava `setPct` a cada evento de scroll, o que disparava um
 * re-render do componente por frame de rolagem — custo direto de INP/TBT na
 * main thread. Agora o handler apenas escreve `style.width` no nó, coalescido
 * em um requestAnimationFrame. O resultado renderizado é o mesmo.
 */
export function ScrollProgress() {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let frame = 0;

    const apply = () => {
      frame = 0;
      const bar = barRef.current;
      if (!bar) return;
      const h = document.documentElement;
      const total = h.scrollHeight - h.clientHeight;
      const p = total > 0 ? (h.scrollTop / total) * 100 : 0;
      bar.style.width = `${p}%`;
    };

    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(apply);
    };

    apply();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    // A TopBar é o primeiro elemento sticky com role="note".
    const topbar =
      (document.querySelector('[role="note"].sticky') as HTMLElement | null) ??
      (document.querySelector('[role="note"]') as HTMLElement | null);
    if (!topbar) return;

    const update = () => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      const h = topbar.getBoundingClientRect().height;
      if (h > 0) wrapper.style.top = `${h}px`;
    };
    update();

    const ro = new ResizeObserver(update);
    ro.observe(topbar);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      className="pointer-events-none fixed left-0 right-0 z-[60] h-[2px] bg-transparent"
      style={{ top: "48px" }}
      aria-hidden="true"
    >
      <div
        ref={barRef}
        className="h-full origin-left transition-[width] duration-100 ease-out"
        style={{
          width: "0%",
          background: "linear-gradient(90deg, #2EA47A 0%, #17724F 40%, #C9A24C 100%)",
          boxShadow: "0 0 12px rgba(46,164,122,0.55)",
        }}
      />
    </div>
  );
}
